(() => {
  'use strict';

  const CART = 'main-cart';
  const DRAWER = 'wub-cart-drawer';
  const SRC = 'wub-cart';
  const WAIT = 180;

  const timers = new Map();
  const pending = new Map();
  const errors = new Map();
  let chain = Promise.resolve();
  let stickyObs;
  let stickyResize;
  let undoTimer;
  let undoNode;

  const mobile = matchMedia('(max-width: 989px)');
  const routes = () => window.WUB?.routes || {};
  const rootUrl = () => window.Shopify?.routes?.root || routes().root_url || '/';
  const changeUrl = () => routes().cart_change_url || `${rootUrl()}cart/change`;
  const addUrl = () => routes().cart_add_url || `${rootUrl()}cart/add`;
  const cartUrl = () => routes().cart_url || `${rootUrl()}cart`;
  const root = () => document.querySelector('[data-wub-cart-page]');
  const dyn = () => root()?.querySelector('[data-wub-cart-page-dynamic]') || null;
  const t = (group, key, fallback = '') => window.WUB?.i18n?.[group]?.[key] || fallback;
  const interpolate = (template, values = {}) => window.WUB?.interpolate
    ? window.WUB.interpolate(template, values)
    : String(template || '').replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (match, key) => Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match);
  const esc = (value) => window.CSS?.escape
    ? CSS.escape(String(value))
    : String(value).replace(/["\\]/g, '\\$&');
  const line = (key) => root()?.querySelector(`[data-wub-cart-line][data-line-key="${esc(key)}"]`) || null;

  function parse(html, selector) {
    return html ? new DOMParser().parseFromString(html, 'text/html').querySelector(selector) : null;
  }

  function norm(input, value) {
    const min = Math.max(1, parseInt(input.min || '1', 10) || 1);
    const maxRaw = parseInt(input.max || '', 10);
    const max = Number.isFinite(maxRaw) ? maxRaw : null;
    const step = Math.max(1, parseInt(input.step || '1', 10) || 1);
    let next = parseInt(value, 10);
    if (!Number.isFinite(next)) next = min;
    next = Math.max(min, next);
    if (max !== null) next = Math.min(max, next);
    next = min + Math.round((next - min) / step) * step;
    return max !== null ? Math.min(max, next) : next;
  }

  function buttons(lineNode, quantity) {
    const input = lineNode?.querySelector('[data-wub-cart-quantity-input]');
    if (!input) return;

    const min = Math.max(1, parseInt(input.min || '1', 10) || 1);
    const maxRaw = parseInt(input.max || '', 10);
    const max = Number.isFinite(maxRaw) ? maxRaw : null;
    const minus = lineNode.querySelector('[data-wub-cart-quantity-action="minus"]');
    const plus = lineNode.querySelector('[data-wub-cart-quantity-action="plus"]');

    if (minus) {
      if (!minus._markup) minus._markup = minus.innerHTML;
      if (!minus._label) minus._label = minus.getAttribute('aria-label') || t('cart', 'decreaseQuantity', 'Decrease quantity');
      const removeAtMin = min === 1 && quantity <= 1;
      const blocked = quantity <= min && !removeAtMin;
      minus.dataset.wubRemoveAtMin = removeAtMin ? 'true' : 'false';
      minus.setAttribute('aria-disabled', blocked ? 'true' : 'false');
      minus.setAttribute('aria-label', removeAtMin ? t('cart', 'removeItem', 'Remove item') : minus._label);

      if (removeAtMin && minus.dataset.wubMode !== 'remove') {
        minus.innerHTML = '<span class="wub-cart-item__remove-symbol" aria-hidden="true">&times;</span>';
        minus.dataset.wubMode = 'remove';
      } else if (!removeAtMin && minus.dataset.wubMode === 'remove') {
        minus.innerHTML = minus._markup;
        minus.dataset.wubMode = 'minus';
      }
    }

    if (plus) plus.setAttribute('aria-disabled', max !== null && quantity >= max ? 'true' : 'false');
  }

  function optimistic(lineNode, quantity) {
    const input = lineNode?.querySelector('[data-wub-cart-quantity-input]');
    if (!input) return;
    input.value = String(quantity);
    const deliveryNode = lineNode.querySelector('[data-wub-cart-delivery]');
    if (deliveryNode) deliveryNode.dataset.quantity = String(quantity);
    buttons(lineNode, quantity);
    delivery();
  }

  function discounts(current, incoming) {
    const currentDiscounts = current.querySelector('.wub-cart-item__discounts');
    const incomingDiscounts = incoming.querySelector('.wub-cart-item__discounts');
    if (currentDiscounts && incomingDiscounts) {
      currentDiscounts.replaceWith(incomingDiscounts.cloneNode(true));
    } else if (!currentDiscounts && incomingDiscounts) {
      current.querySelector('.wub-cart-item__actions')?.before(incomingDiscounts.cloneNode(true));
    } else if (currentDiscounts && !incomingDiscounts) {
      currentDiscounts.remove();
    }
  }

  function qtySync(current, incoming, key) {
    const currentInput = current.querySelector('[data-wub-cart-quantity-input]');
    const incomingInput = incoming.querySelector('[data-wub-cart-quantity-input]');
    if (!currentInput || !incomingInput) return;

    ['min', 'max', 'step'].forEach((attribute) => {
      if (incomingInput.hasAttribute(attribute)) currentInput.setAttribute(attribute, incomingInput.getAttribute(attribute));
      else currentInput.removeAttribute(attribute);
    });

    const server = parseInt(incomingInput.value || '1', 10) || 1;
    current.dataset.confirmedQuantity = String(server);
    const quantity = pending.has(key) ? pending.get(key) : server;
    currentInput.value = String(quantity);
    const deliveryNode = current.querySelector('[data-wub-cart-delivery]');
    if (deliveryNode) deliveryNode.dataset.quantity = String(quantity);
    buttons(current, quantity);
  }

  function patch(html) {
    const incomingDynamic = parse(html, '[data-wub-cart-page-dynamic]');
    const currentDynamic = dyn();
    if (!incomingDynamic || !currentDynamic) return false;

    const currentLines = [...currentDynamic.querySelectorAll('[data-wub-cart-line]')];
    const incomingLines = [...incomingDynamic.querySelectorAll('[data-wub-cart-line]')];
    if (currentLines.length !== incomingLines.length) return false;

    const incomingMap = new Map(incomingLines.map((item) => [item.dataset.lineKey, item]));
    if (currentLines.some((item) => !incomingMap.has(item.dataset.lineKey))) return false;

    currentLines.forEach((currentLine) => {
      const incomingLine = incomingMap.get(currentLine.dataset.lineKey);
      const currentTop = currentLine.querySelector('.wub-cart-item__top');
      const incomingTop = incomingLine.querySelector('.wub-cart-item__top');
      if (currentTop && incomingTop) currentTop.innerHTML = incomingTop.innerHTML;
      discounts(currentLine, incomingLine);
      qtySync(currentLine, incomingLine, currentLine.dataset.lineKey);
    });

    const currentPrices = currentDynamic.querySelector('.wub-cart-summary__prices');
    const incomingPrices = incomingDynamic.querySelector('.wub-cart-summary__prices');
    if (currentPrices && incomingPrices) currentPrices.innerHTML = incomingPrices.innerHTML;

    const currentSticky = currentDynamic.querySelector('[data-wub-cart-sticky-total]');
    const incomingSticky = incomingDynamic.querySelector('[data-wub-cart-sticky-total]');
    if (currentSticky && incomingSticky) currentSticky.innerHTML = incomingSticky.innerHTML;
    return true;
  }

  function replace(html) {
    const incoming = parse(html, '[data-wub-cart-page-dynamic]');
    const current = dyn();
    if (!incoming || !current) return false;
    const review = current.querySelector('[data-wakeup-cart-reviews]');
    current.replaceWith(incoming);
    mount(review);
    return true;
  }

  function emit(data, action) {
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: {
        source: SRC,
        action,
        item_count: data?.item_count ?? null,
        total_price: data?.total_price ?? null
      }
    }));
  }

  function lineError(lineNode, message, stateName = 'error') {
    const host = lineNode?.querySelector('[data-wub-cart-line-error]');
    if (!host) return;
    host.textContent = message || '';
    host.hidden = !message;
    if (message) {
      host.dataset.state = stateName;
      host.setAttribute('role', stateName === 'error' ? 'alert' : 'status');
      host.setAttribute('aria-live', 'polite');
    } else {
      delete host.dataset.state;
      host.removeAttribute('role');
      host.removeAttribute('aria-live');
    }
  }

  function inventoryLimitMessage(value) {
    return /only\s+\d+.*added.*availab|maximum quantity|max quantity|can(?:not|'t) add more|not enough stock/i.test(String(value || ''));
  }

  async function state() {
    const response = await fetch(`${cartUrl()}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!response.ok) throw new Error(`Cart state failed (${response.status})`);
    return response.json();
  }

  async function snapshot(lineNode) {
    const input = lineNode?.querySelector('[data-wub-cart-quantity-input]');
    const key = lineNode?.dataset.lineKey;
    if (!lineNode || !input || !key) return null;

    try {
      const cart = await state();
      const item = cart.items?.find((entry) => entry.key === key);
      if (!item) return null;
      return {
        id: Number(item.variant_id || item.id || 0),
        qty: Math.max(1, parseInt(input.value || item.quantity || '1', 10) || 1),
        props: item.properties || {},
        plan: item.selling_plan_allocation?.selling_plan?.id || ''
      };
    } catch (error) {
      console.warn('WUB removal snapshot failed', error);
      return null;
    }
  }

  function undoDismiss() {
    clearTimeout(undoTimer);
    undoTimer = null;
    if (!undoNode) return;
    const node = undoNode;
    undoNode = null;
    node.classList.remove('is-visible');
    setTimeout(() => node.remove(), 180);
  }

  async function restore(snapshotData, button) {
    if (!snapshotData?.id) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const item = { id: snapshotData.id, quantity: snapshotData.qty };
      if (Object.keys(snapshotData.props || {}).length) item.properties = snapshotData.props;
      if (snapshotData.plan) item.selling_plan = snapshotData.plan;

      const response = await fetch(`${addUrl()}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [item], sections: [CART, DRAWER], sections_url: location.pathname })
      });
      const data = await response.json();
      if (!response.ok || data.status || data.errors) {
        throw new Error(data.description || data.message || data.errors || t('cart', 'restoreFailed', 'Could not restore the item. Please try again.'));
      }

      if (data.sections?.[CART]) replace(data.sections[CART]);
      if (data.sections?.[DRAWER] && window.WUBCommerce?.applyDrawerHtml) {
        window.WUBCommerce.applyDrawerHtml(data.sections[DRAWER]);
      }
      undoDismiss();
      emit(data, 'restore');
    } catch (error) {
      console.error('WUB undo failed', error);
      button.disabled = false;
      button.removeAttribute('aria-busy');
      const text = undoNode?.querySelector('[data-wub-cart-undo-text]');
      if (text) text.textContent = t('cart', 'restoreFailed', 'Could not restore the item. Please try again.');
    }
  }

  function undoShow(snapshotData) {
    undoDismiss();
    const node = document.createElement('div');
    node.className = 'wub-cart-undo';
    node.dataset.wubCartUndo = '';
    node.setAttribute('role', 'status');
    node.setAttribute('aria-live', 'polite');
    node.setAttribute('aria-atomic', 'true');

    const text = document.createElement('span');
    text.dataset.wubCartUndoText = '';
    text.textContent = t('cart', 'itemRemoved', 'Item removed');

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'wub-cart-undo__button';
    button.textContent = t('cart', 'undo', 'Undo');
    button.addEventListener('click', () => restore(snapshotData, button), { once: true });

    node.append(text, button);
    document.body.append(node);
    undoNode = node;
    requestAnimationFrame(() => node.classList.add('is-visible'));
    undoTimer = setTimeout(undoDismiss, 6500);
  }

  async function change(key, quantity) {
    const lineNode = line(key);
    lineNode?.setAttribute('aria-busy', 'true');
    lineError(lineNode, '');

    try {
      const response = await fetch(`${changeUrl()}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity, sections: [CART, DRAWER], sections_url: location.pathname })
      });
      const data = await response.json();
      if (!response.ok || data.errors) {
        throw new Error(data.description || data.message || data.errors || t('cart', 'updateFailed', 'Could not update your cart.'));
      }

      errors.delete(key);
      if (pending.get(key) === quantity) pending.delete(key);

      const html = data.sections?.[CART];
      let cartRendered = false;
      if (html) {
        if (quantity > 0) {
          cartRendered = patch(html) || replace(html);
        } else {
          cartRendered = replace(html);
        }
      }

      if (!cartRendered) {
        window.location.reload();
        return true;
      }

      const drawerHtml = data.sections?.[DRAWER];
      if (drawerHtml && window.WUBCommerce?.applyDrawerHtml) {
        window.WUBCommerce.applyDrawerHtml(drawerHtml);
      }

      emit(data, quantity === 0 ? 'remove' : 'change');
      if (quantity > 0) {
        mountQty();
        delivery();
        stickyHeight();
      }
      return true;
    } catch (error) {
      console.error('WUB cart change failed', error);
      const raw = error.message || t('cart', 'updateFailed', 'Could not update your cart.');
      const currentLine = line(key);
      pending.delete(key);
      let confirmed = currentLine ? (parseInt(currentLine.dataset.confirmedQuantity || '1', 10) || 1) : 1;
      let inventoryLimited = inventoryLimitMessage(raw);

      try {
        const cart = await state();
        const item = cart.items?.find((entry) => entry.key === key);
        if (item) {
          confirmed = Math.max(1, parseInt(item.quantity || confirmed, 10) || confirmed);
          if (quantity > confirmed) inventoryLimited = true;
        }
      } catch (fetchError) {
        console.warn('WUB cart error state refresh failed', fetchError);
      }

      if (inventoryLimited) {
        errors.delete(key);
        if (currentLine) {
          currentLine.dataset.confirmedQuantity = String(confirmed);
          optimistic(currentLine, confirmed);
        }
        lineError(
          currentLine,
          interpolate(t('cart', 'inventoryLimit', 'A maximum of {{ quantity }} is available. Your cart has been set to {{ quantity }}.'), { quantity: confirmed }),
          'warning'
        );
      } else {
        errors.set(key, error);
        if (currentLine) optimistic(currentLine, confirmed);
        lineError(currentLine, raw, 'error');
      }

      checkoutState();
      return false;
    } finally {
      line(key)?.removeAttribute('aria-busy');
    }
  }

  function enqueue(key, quantity) {
    chain = chain.catch(() => false).then(() => change(key, quantity));
    return chain;
  }

  function schedule(key, quantity, wait = WAIT) {
    if (!key) return;
    const safeQuantity = Math.max(1, parseInt(quantity, 10) || 1);
    pending.set(key, safeQuantity);
    clearTimeout(timers.get(key));
    timers.set(key, setTimeout(() => {
      timers.delete(key);
      const value = pending.get(key);
      if (value != null) enqueue(key, value);
    }, Math.max(0, wait)));
  }

  async function remove(key) {
    const lineNode = line(key);
    if (!lineNode || lineNode.dataset.wubRemoving === 'true') return;
    lineNode.dataset.wubRemoving = 'true';
    const snapshotData = await snapshot(lineNode);
    clearTimeout(timers.get(key));
    timers.delete(key);
    pending.delete(key);
    const ok = await enqueue(key, 0);
    if (ok && snapshotData) undoShow(snapshotData);
    else line(key)?.removeAttribute('data-wub-removing');
  }

  async function flush() {
    [...timers.keys()].forEach((key) => {
      clearTimeout(timers.get(key));
      timers.delete(key);
      const quantity = pending.get(key);
      if (quantity != null) enqueue(key, quantity);
    });
    await chain;
    delivery();
    checkoutState();
    if (errors.size) throw [...errors.values()][0];
    return true;
  }

  function deliveryPromise() {
    return window.WUBDelivery?.getPromise?.() || {
      enabled: false,
      shippingToday: false,
      label: '',
      secondsToCutoff: 0
    };
  }

  function stickyDelivery(rootNode) {
    const total = rootNode?.querySelector('.wub-cart-sticky__total');
    if (!total) return null;
    let host = total.querySelector('[data-wub-cart-sticky-delivery]');
    if (!host) {
      host = document.createElement('span');
      host.className = 'wub-cart-sticky__delivery';
      host.dataset.wubCartStickyDelivery = '';
      host.hidden = true;
      total.appendChild(host);
    }
    return host;
  }

  function cutoff(allReady, promise) {
    const host = root()?.querySelector('[data-wub-cart-cutoff]');
    if (!host) return;

    if (!allReady || !promise?.enabled || !promise.shippingToday || !(promise.secondsToCutoff > 0)) {
      if (host.textContent) host.textContent = '';
      host.hidden = true;
      return;
    }

    const totalMinutes = Math.max(1, Math.ceil(promise.secondsToCutoff / 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const hourUnit = t('cart', 'hoursShort', 'h');
    const minuteUnit = t('cart', 'minutesShort', 'min');
    const remaining = hours
      ? `${hours} ${hourUnit}${minutes ? ` ${minutes} ${minuteUnit}` : ''}`
      : `${minutes} ${minuteUnit}`;
    const next = interpolate(t('cart', 'orderWithinToday', 'Order within {{ remaining }} for dispatch today'), { remaining });
    if (host.textContent !== next) host.textContent = next;
    host.hidden = false;
  }

  function delivery() {
    const rootNode = root();
    if (!rootNode) return;

    const promise = deliveryPromise();
    const hosts = [...rootNode.querySelectorAll('[data-wub-cart-delivery]')];
    let ready = false;
    let late = false;
    let out = false;
    const lateTexts = [];

    hosts.forEach((host) => {
      const available = host.dataset.available === 'true';
      const tracked = host.dataset.tracked === 'true';
      const inventory = parseInt(host.dataset.inventory || '0', 10) || 0;
      const quantity = parseInt(host.dataset.quantity || '1', 10) || 1;
      const continueSelling = host.dataset.policy === 'continue';
      const message = host.dataset.deliveryMessage || '';
      const fallback = host.dataset.fallback || '';
      const lateLabel = t('delivery', 'withLeadTime', 'with lead time');
      let text = '';
      let stateName = 'ready';

      host.dataset.wubReady = 'false';
      host.dataset.wubDelayed = 'false';

      if (!available) {
        text = t('stock', 'outOfStock', 'Out of stock');
        stateName = 'unavailable';
        out = true;
      } else if (!tracked || inventory >= quantity) {
        text = promise.enabled && promise.label
          ? interpolate(t('delivery', 'readyExpected', 'In stock · expected delivery {{ date }}'), { date: promise.label })
          : t('stock', 'inStock', 'In stock');
        ready = true;
        host.dataset.wubReady = 'true';
      } else if (inventory > 0 && continueSelling) {
        const delayedQuantity = Math.max(0, quantity - inventory);
        if (promise.enabled && promise.label) {
          text = interpolate(t('delivery', 'splitExpected', '{{ available }} expected delivery {{ date }} · {{ backorder }} {{ lead_time }}'), {
            available: inventory,
            date: promise.label,
            backorder: delayedQuantity,
            lead_time: lateLabel
          });
        } else {
          text = t('stock', 'splitTemplate', '[available] ready to ship · [backorder] with lead time')
            .replace('[available]', String(inventory))
            .replace('[backorder]', String(delayedQuantity));
        }
        stateName = 'delayed';
        ready = true;
        late = true;
        lateTexts.push(text);
        host.dataset.wubReady = 'true';
        host.dataset.wubDelayed = 'true';
      } else if (continueSelling) {
        text = message || fallback || lateLabel;
        stateName = 'delayed';
        late = true;
        lateTexts.push(text);
        host.dataset.wubDelayed = 'true';
      } else {
        text = t('stock', 'outOfStock', 'Out of stock');
        stateName = 'unavailable';
        out = true;
      }

      if (host.textContent !== text) host.textContent = text;
      if (host.dataset.state !== stateName) host.dataset.state = stateName;
    });

    const note = rootNode.querySelector('[data-wub-cart-delivery-summary]');
    if (note) {
      const show = rootNode.dataset.splitNoteEnabled === 'true' && ready && late;
      const text = show ? (rootNode.dataset.splitNote || '') : '';
      if (note.textContent !== text) note.textContent = text;
      note.hidden = !show;
    }

    const sticky = stickyDelivery(rootNode);
    if (sticky) {
      const uniqueLateTexts = [...new Set(lateTexts.filter(Boolean))];
      let text = '';
      let stateName = 'ready';

      if (out) {
        text = t('cart', 'checkAvailability', 'Check availability');
        stateName = 'unavailable';
      } else if (ready && late) {
        text = t('cart', 'partialDelivery', 'Split delivery');
        if (promise.enabled && promise.label) {
          const expected = interpolate(t('cart', 'firstPartExpected', 'first part expected {{ date }}'), { date: promise.label });
          text = `${text} · ${expected}`;
        }
        stateName = 'mixed';
      } else if (late) {
        text = uniqueLateTexts.length === 1 ? uniqueLateTexts[0] : t('cart', 'deliveryVaries', 'Lead time varies by item');
        stateName = 'delayed';
      } else if (ready) {
        text = promise.enabled && promise.label
          ? interpolate(t('cart', 'expectedDelivered', 'Expected delivery {{ date }}'), { date: promise.label })
          : t('stock', 'inStock', 'In stock');
      }

      if (sticky.textContent !== text) sticky.textContent = text;
      if (sticky.dataset.state !== stateName) sticky.dataset.state = stateName;
      sticky.hidden = !text;
    }

    cutoff(hosts.length > 0 && hosts.every((host) => host.dataset.state === 'ready'), promise);
    checkoutState();
  }

  function checkoutError(message = '') {
    const host = root()?.querySelector('[data-wub-cart-checkout-error]');
    if (host) {
      host.textContent = message;
      host.hidden = !message;
    }
  }

  function blockReason() {
    if (root()?.querySelector('[data-wub-cart-delivery][data-state="unavailable"]')) {
      return t('cart', 'unavailableCheckout', 'An item in your cart is no longer available. Remove it to continue.');
    }
    if (errors.size) {
      return t('cart', 'checkItemMessage', 'Check the item message before continuing to checkout.');
    }
    return '';
  }

  function checkoutState() {
    const rootNode = root();
    if (!rootNode) return true;
    const reason = blockReason();
    const blocked = Boolean(reason);
    rootNode.querySelectorAll('[data-wub-cart-checkout]').forEach((button) => {
      button.disabled = blocked;
      button.setAttribute('aria-disabled', blocked ? 'true' : 'false');
    });
    checkoutError(reason);
    return !blocked;
  }

  function assertReady() {
    delivery();
    const reason = blockReason();
    if (reason) {
      checkoutError(reason);
      root()?.querySelector('[data-wub-cart-checkout-error]')?.focus();
      throw new Error(reason);
    }
    return true;
  }

  function stickyPublish(sticky) {
    if (!sticky || sticky.hidden) return;
    const height = Math.ceil(sticky.getBoundingClientRect().height);
    if (height > 0) document.documentElement.style.setProperty('--wub-cart-sticky-height', `${height}px`);
  }

  function stickyHeight() {
    const sticky = root()?.querySelector('[data-wub-cart-sticky]');
    if (sticky && !sticky.hidden) requestAnimationFrame(() => stickyPublish(sticky));
  }

  function stickyMount() {
    stickyObs?.disconnect();
    stickyResize?.disconnect();
    stickyObs = null;
    stickyResize = null;
    document.body.classList.remove('wub-cart-sticky-visible');

    const rootNode = root();
    const sticky = rootNode?.querySelector('[data-wub-cart-sticky]');
    const anchor = rootNode?.querySelector('[data-wub-cart-main-checkout-anchor]');
    if (!sticky || !anchor || !mobile.matches) {
      if (sticky) sticky.hidden = true;
      return;
    }

    const render = (anchorVisible) => {
      const show = mobile.matches && !anchorVisible;
      sticky.hidden = !show;
      document.body.classList.toggle('wub-cart-sticky-visible', show);
      if (show) requestAnimationFrame(() => stickyPublish(sticky));
    };

    if ('ResizeObserver' in window) {
      stickyResize = new ResizeObserver(() => stickyPublish(sticky));
      stickyResize.observe(sticky);
    }

    if ('IntersectionObserver' in window) {
      stickyObs = new IntersectionObserver((entries) => render(entries.some((entry) => entry.isIntersecting)), { threshold: 0.15 });
      stickyObs.observe(anchor);
    } else {
      render(false);
    }
  }

  function mountQty() {
    dyn()?.querySelectorAll('[data-wub-cart-line]').forEach((lineNode) => {
      const input = lineNode.querySelector('[data-wub-cart-quantity-input]');
      if (!input) return;
      if (!lineNode.dataset.confirmedQuantity) {
        lineNode.dataset.confirmedQuantity = String(parseInt(input.value || '1', 10) || 1);
      }
      const pendingQuantity = pending.get(lineNode.dataset.lineKey);
      const quantity = Number.isFinite(pendingQuantity) ? pendingQuantity : norm(input, input.value);
      input.value = String(quantity);
      const deliveryNode = lineNode.querySelector('[data-wub-cart-delivery]');
      if (deliveryNode) deliveryNode.dataset.quantity = String(quantity);
      buttons(lineNode, quantity);
    });
  }

  function setupCheckout() {
    root()?.querySelectorAll('[data-wub-checkout][data-wub-checkout-source^="cart"]').forEach((button) => {
      button.removeAttribute('data-wub-checkout');
      button.dataset.wubCartCheckout = '';
    });
  }

  function zone(review) {
    const rootNode = root();
    const summary = rootNode?.querySelector('.wub-cart-summary');
    const form = summary?.querySelector('[data-wub-cart-main-checkout-anchor]');
    if (!summary || !form) return;

    let cut = summary.querySelector('[data-wub-cart-cutoff]');
    if (!cut) {
      cut = document.createElement('p');
      cut.className = 'wub-cart-summary__cutoff';
      cut.dataset.wubCartCutoff = '';
      cut.hidden = true;
      form.before(cut);
    }

    let proof = summary.querySelector('[data-wub-cart-purchase-proof]');
    if (!proof) {
      proof = document.createElement('div');
      proof.className = 'wub-cart-summary__purchase-proof';
      proof.dataset.wubCartPurchaseProof = '';
      cut.after(proof);
    }

    const reviewNode = review || rootNode.querySelector('[data-wakeup-cart-reviews]') || document.querySelector('[data-wakeup-cart-reviews]');
    if (reviewNode && !proof.contains(reviewNode)) proof.prepend(reviewNode);

    if (!proof.querySelector('[data-wub-cart-trust]')) {
      const trust = document.createElement('div');
      trust.className = 'wub-cart-summary__trust';
      trust.dataset.wubCartTrust = '';

      const returns = document.createElement('span');
      returns.textContent = `✓ ${t('cart', 'freeReturnsShort', 'Free 30-day returns')}`;
      const keur = document.createElement('span');
      keur.textContent = `✓ ${t('cart', 'webwinkelkeurCertified', 'WebwinkelKeur certified')}`;
      trust.append(returns, keur);
      proof.append(trust);
    }

    let error = summary.querySelector('[data-wub-cart-checkout-error]');
    if (!error) {
      error = document.createElement('p');
      error.className = 'wub-cart-summary__checkout-error';
      error.dataset.wubCartCheckoutError = '';
      error.setAttribute('role', 'alert');
      error.tabIndex = -1;
      error.hidden = true;
      proof.after(error);
    }

    const terms = summary.querySelector('.wub-cart-summary__terms');
    if (terms && terms.previousElementSibling !== form) form.after(terms);

    const icons = summary.querySelector('.wub-cart-pay__icons');
    if (icons && !icons.querySelector('[data-wub-payment-more]')) {
      const more = document.createElement('span');
      more.className = 'wub-cart-pay__more';
      more.dataset.wubPaymentMore = '';
      more.textContent = t('cart', 'more', '+ more');
      icons.append(more);
    }
  }

  function mount(review = null) {
    setupCheckout();
    zone(review);
    mountQty();
    delivery();
    stickyMount();
    window.WakeupCartReviews?.mountAll?.();
  }

  async function checkout(button) {
    const form = button.closest('form');
    if (!form || button.dataset.wubCheckoutSubmitting === 'true') return;
    button.dataset.wubCheckoutSubmitting = 'true';
    form.setAttribute('aria-busy', 'true');
    checkoutError('');

    try {
      await flush();
      assertReady();
      document.dispatchEvent(new CustomEvent('wub:checkout-click', {
        detail: { source: button.dataset.wubCheckoutSource || 'cart' }
      }));
      form.requestSubmit(button);
    } catch (error) {
      console.warn('WUB checkout blocked', error);
      form.removeAttribute('aria-busy');
      delete button.dataset.wubCheckoutSubmitting;
      checkoutState();
    }
  }

  document.addEventListener('click', (event) => {
    const lineNode = event.target.closest?.('[data-wub-cart-line]');
    if (lineNode) {
      const key = lineNode.dataset.lineKey;
      const removeButton = event.target.closest?.('[data-wub-cart-remove]');
      if (removeButton) {
        event.preventDefault();
        remove(key);
        return;
      }

      const action = event.target.closest?.('[data-wub-cart-quantity-action]');
      if (action) {
        event.preventDefault();
        if (action.getAttribute('aria-disabled') === 'true') return;
        const input = lineNode.querySelector('[data-wub-cart-quantity-input]');
        if (!input) return;

        const current = norm(input, input.value);
        const step = Math.max(1, parseInt(input.step || '1', 10) || 1);
        const min = Math.max(1, parseInt(input.min || '1', 10) || 1);
        if (action.dataset.wubCartQuantityAction === 'minus' && min === 1 && current <= 1) {
          remove(key);
          return;
        }

        const next = action.dataset.wubCartQuantityAction === 'plus'
          ? norm(input, current + step)
          : norm(input, Math.max(min, current - step));
        if (next !== current) {
          lineError(lineNode, '');
          errors.delete(key);
          optimistic(lineNode, next);
          schedule(key, next);
        }
        return;
      }
    }

    const checkoutButton = event.target.closest?.('[data-wub-cart-checkout]');
    if (checkoutButton) {
      event.preventDefault();
      if (checkoutButton.disabled || checkoutButton.getAttribute('aria-disabled') === 'true') {
        checkoutState();
        return;
      }
      checkout(checkoutButton);
    }
  });

  document.addEventListener('focusin', (event) => {
    const input = event.target.closest?.('[data-wub-cart-quantity-input]');
    if (input && !input.dataset.wubSelected) {
      input.dataset.wubSelected = 'true';
      requestAnimationFrame(() => input.select?.());
    }
  });

  document.addEventListener('focusout', (event) => {
    const input = event.target.closest?.('[data-wub-cart-quantity-input]');
    if (input) delete input.dataset.wubSelected;
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest?.('[data-wub-cart-quantity-input]');
    if (!input) return;
    const lineNode = input.closest('[data-wub-cart-line]');
    const key = lineNode?.dataset.lineKey;
    if (!lineNode || !key) return;

    const quantity = norm(input, input.value);
    lineError(lineNode, '');
    errors.delete(key);
    optimistic(lineNode, quantity);
    schedule(key, quantity, 0);
  });

  document.addEventListener('wub:delivery-synced', () => {
    delivery();
    stickyHeight();
  });

  if (mobile.addEventListener) mobile.addEventListener('change', stickyMount);
  else mobile.addListener?.(stickyMount);

  document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  document.addEventListener('shopify:section:load', () => mount());
  window.addEventListener('pageshow', () => mount());

  window.WUBCart = Object.freeze({
    flushPendingChanges: flush,
    syncCheckoutAvailability: checkoutState,
    canCheckout: () => !blockReason(),
    showCheckoutError: checkoutError,
    renderDelivery: delivery
  });

  if (document.readyState !== 'loading') mount();
})();
